import React, { useState, useRef, useEffect } from 'react';
import {
  formatCalendarDate,
  parseCalendarDate,
} from '../utils/calendarDate.js';
import './CustomDatePicker.css';

const CustomDatePicker = ({ value, onChange, label, min, max, opensAbove = false, compact = false, compactAlign = 'right', allowFuture = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(() => parseCalendarDate(value) || new Date());
  const [view, setView] = useState('date'); // 'date', 'month', 'year'
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const isSelectingFromCalendar = useRef(false);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthsShort = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const parseFlexibleDate = (dateString) => {
    if (!dateString || !dateString.trim()) return null;
    
    const trimmed = dateString.trim();
    
    // Find the first non-digit separator character
    const separatorMatch = trimmed.match(/[.\-\/]/);
    if (!separatorMatch) {
      // No separator found, try default parsing
      const date = new Date(trimmed);
      return !isNaN(date.getTime()) ? date : null;
    }
    
    const separator = separatorMatch[0];
    const parts = trimmed.split(separator);
    
    if (parts.length !== 3) {
      // Invalid format, try default parsing
      const date = new Date(trimmed);
      return !isNaN(date.getTime()) ? date : null;
    }
    
    // Parse parts as numbers
    const nums = parts.map(p => parseInt(p, 10));
    if (nums.some(n => isNaN(n))) {
      return null;
    }
    
    let day, month, year;
    
    // Determine format based on the values
    // If first part is > 31 or is 4 digits, it's likely YYYY-MM-DD
    if (nums[0] > 31 || parts[0].length === 4) {
      // YYYY-MM-DD format
      year = nums[0];
      month = nums[1] - 1; // JS months are 0-indexed
      day = nums[2];
    } else {
      // DD-MM-YYYY or DD-MM-YY format
      day = nums[0];
      month = nums[1] - 1; // JS months are 0-indexed
      year = nums[2];
      
      // Handle 2-digit year
      if (year < 100) {
        // Assume years 00-49 are 2000-2049, 50-99 are 1950-1999
        year += year < 50 ? 2000 : 1900;
      }
    }
    
    // Validate ranges
    if (month < 0 || month > 11) return null;
    if (day < 1 || day > 31) return null;
    if (year < 1900 || year > 2100) return null;
    
    // Create date and validate it's real (e.g., not Feb 31)
    const date = new Date(year, month, day);
    if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
      return null;
    }
    
    return date;
  };

  const finishEditing = () => {
    if (editValue) {
      // Try to parse the date using flexible parser
      const parsedDate = parseFlexibleDate(editValue);
      if (parsedDate && !isNaN(parsedDate.getTime())) {
        const formattedDate = formatDate(parsedDate);
        if (!isDateDisabled(parsedDate)) {
          onChange(formattedDate);
          // Update currentMonth to reflect the new date
          setCurrentMonth(parsedDate);
        }
      }
    }
    setIsEditing(false);
    setEditValue('');
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setIsEditing(false);
        setEditValue('');
        setView('date'); // Reset to date view when closing
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;

    setView('date');
    // The visible month is local state, so an external value change (for example
    // "Use Today") would otherwise leave the calendar on the previous month.
    const selected = parseCalendarDate(value);
    if (!selected) return;
    setCurrentMonth((prev) => {
      if (prev.getFullYear() === selected.getFullYear() && prev.getMonth() === selected.getMonth()) {
        return prev;
      }
      return new Date(selected.getFullYear(), selected.getMonth(), 1);
    });
  }, [isOpen, value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const formatDate = (date) => formatCalendarDate(date);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return 'Select date';
    const date = parseCalendarDate(dateString);
    if (!date) return dateString;
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatForEditing = (dateString) => {
    if (!dateString) return '';
    const date = parseCalendarDate(dateString);
    if (!date) return dateString;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for previous month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getYearsInDecade = (date) => {
    const year = date.getFullYear();
    const startYear = Math.floor(year / 12) * 12;
    const years = [];
    
    for (let i = 0; i < 12; i++) {
      years.push(startYear + i);
    }
    
    return years;
  };

  const isDateDisabled = (date) => {
    if (!date) return true;
    if (!allowFuture) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      if (date > today) return true;
    }
    const minDate = parseCalendarDate(min);
    const maxDate = parseCalendarDate(max);
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;

    return false;
  };

  const isMonthDisabled = (year, month) => {
    const today = new Date();
    const lastDayOfMonth = new Date(year, month + 1, 0);
    lastDayOfMonth.setHours(23, 59, 59, 999);
    const minDate = parseCalendarDate(min);
    const maxDate = parseCalendarDate(max);

    if (!allowFuture && lastDayOfMonth > today && new Date(year, month, 1) > today) return true;
    if (minDate && lastDayOfMonth < minDate) return true;
    if (maxDate && new Date(year, month, 1) > maxDate) return true;

    return false;
  };

  const isYearDisabled = (year) => {
    const today = new Date();
    const lastDayOfYear = new Date(year, 11, 31);
    lastDayOfYear.setHours(23, 59, 59, 999);
    const minDate = parseCalendarDate(min);
    const maxDate = parseCalendarDate(max);

    if (!allowFuture && lastDayOfYear > today && new Date(year, 0, 1) > today) return true;
    if (minDate && lastDayOfYear < minDate) return true;
    if (maxDate && new Date(year, 0, 1) > maxDate) return true;

    return false;
  };

  const isDateSelected = (date) => {
    if (!date || !value) return false;
    return formatDate(date) === value;
  };

  const isMonthSelected = (year, month) => {
    if (!value) return false;
    const selectedDate = parseCalendarDate(value);
    if (!selectedDate) return false;
    return selectedDate.getFullYear() === year && selectedDate.getMonth() === month;
  };

  const isYearSelected = (year) => {
    if (!value) return false;
    const selectedDate = parseCalendarDate(value);
    if (!selectedDate) return false;
    return selectedDate.getFullYear() === year;
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (year, month) => {
    const today = new Date();
    return today.getFullYear() === year && today.getMonth() === month;
  };

  const isCurrentYear = (year) => {
    const today = new Date();
    return today.getFullYear() === year;
  };

  const handleDateSelect = (date) => {
    if (isDateDisabled(date)) return;
    isSelectingFromCalendar.current = true;
    onChange(formatDate(date));
    setIsOpen(false);
    setIsEditing(false);
    setEditValue('');
    setTimeout(() => {
      isSelectingFromCalendar.current = false;
    }, 100);
  };

  const handleMonthSelect = (month) => {
    const year = currentMonth.getFullYear();
    if (isMonthDisabled(year, month)) return;
    isSelectingFromCalendar.current = true;
    setCurrentMonth(new Date(year, month, 1));
    setView('date');
    setTimeout(() => {
      isSelectingFromCalendar.current = false;
    }, 100);
  };

  const handleYearSelect = (year) => {
    if (isYearDisabled(year)) return;
    isSelectingFromCalendar.current = true;
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setView('month');
    setTimeout(() => {
      isSelectingFromCalendar.current = false;
    }, 100);
  };

  const handleDisplayClick = (e) => {
    if (!isOpen && !isEditing) {
      // Open calendar and enable editing simultaneously
      setIsOpen(true);
      setIsEditing(true);
      setEditValue(formatForEditing(value));
    } else if (isOpen && !isEditing) {
      // If calendar is open but not editing, enable editing
      setIsEditing(true);
      setEditValue(formatForEditing(value));
    } else if (!isOpen && isEditing) {
      // If editing but calendar closed, open calendar
      setIsOpen(true);
    } else {
      // Both are open, toggle calendar off
      setIsOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleInputBlur = (e) => {
    // Don't finish editing if selecting from calendar
    if (isSelectingFromCalendar.current) {
      return;
    }
    // Don't finish editing if clicking inside the calendar dropdown
    if (e.relatedTarget && dropdownRef.current?.contains(e.relatedTarget)) {
      return;
    }
    // Use a small timeout to allow calendar clicks to process
    setTimeout(() => {
      if (!isSelectingFromCalendar.current) {
        finishEditing();
      }
    }, 150);
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      finishEditing();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue('');
    }
  };

  const navigate = (direction) => {
    isSelectingFromCalendar.current = true;
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      if (view === 'date') {
        newDate.setMonth(prev.getMonth() + direction);
      } else if (view === 'month') {
        newDate.setFullYear(prev.getFullYear() + direction);
      } else if (view === 'year') {
        newDate.setFullYear(prev.getFullYear() + (direction * 12));
      }
      return newDate;
    });
    setTimeout(() => {
      isSelectingFromCalendar.current = false;
    }, 100);
  };

  const handleHeaderClick = () => {
    isSelectingFromCalendar.current = true;
    if (view === 'date') {
      setView('month');
    } else if (view === 'month') {
      setView('year');
    }
    setTimeout(() => {
      isSelectingFromCalendar.current = false;
    }, 100);
  };

  const getHeaderText = () => {
    if (view === 'date') {
      return `${months[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    } else if (view === 'month') {
      return currentMonth.getFullYear();
    } else if (view === 'year') {
      const years = getYearsInDecade(currentMonth);
      return `${years[0]} - ${years[years.length - 1]}`;
    }
  };

  const days = getDaysInMonth(currentMonth);
  const years = getYearsInDecade(currentMonth);

  const calendarSvg = (
    <svg className="calendar-icon" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
    </svg>
  );

  if (compact) {
    return (
      <div className="custom-date-picker compact-date-picker" ref={dropdownRef}>
        <button
          type="button"
          className={`compact-date-trigger${value ? ' has-date' : ''}`}
          onClick={() => setIsOpen(prev => !prev)}
          title={value ? formatDisplayDate(value) : 'Set date'}
          aria-label={value ? `Date: ${formatDisplayDate(value)}` : 'Set date'}
        >
          {calendarSvg}
        </button>

        {isOpen && (
          <div className={`calendar-dropdown compact-calendar-dropdown compact-align-${compactAlign}${opensAbove ? ' opens-above' : ''}`}>
            <div className="calendar-header">
              <button type="button" onClick={() => navigate(-1)} onMouseDown={(e) => e.preventDefault()} className="nav-button">‹</button>
              <h3 className={view !== 'year' ? 'clickable' : ''} onClick={handleHeaderClick} onMouseDown={(e) => e.preventDefault()}>
                {getHeaderText()}
              </h3>
              <button type="button" onClick={() => navigate(1)} onMouseDown={(e) => e.preventDefault()} className="nav-button">›</button>
            </div>

            {view === 'date' && (
              <div className="calendar-grid">
                <div className="weekdays">
                  {weekdays.map(day => <div key={day} className="weekday">{day}</div>)}
                </div>
                <div className="days">
                  {days.map((date, index) => (
                    <button key={index} type="button"
                      className={`day ${!date ? 'empty' : ''} ${isDateSelected(date) ? 'selected' : ''} ${isDateDisabled(date) ? 'disabled' : ''} ${isToday(date) ? 'today' : ''}`}
                      onClick={() => date && handleDateSelect(date)}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={!date || isDateDisabled(date)}
                    >
                      {date && date.getDate()}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {view === 'month' && (
              <div className="calendar-grid month-grid">
                <div className="months">
                  {monthsShort.map((month, index) => (
                    <button key={index} type="button"
                      className={`month ${isMonthSelected(currentMonth.getFullYear(), index) ? 'selected' : ''} ${isMonthDisabled(currentMonth.getFullYear(), index) ? 'disabled' : ''} ${isCurrentMonth(currentMonth.getFullYear(), index) ? 'today' : ''}`}
                      onClick={() => handleMonthSelect(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={isMonthDisabled(currentMonth.getFullYear(), index)}
                    >
                      {month}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {view === 'year' && (
              <div className="calendar-grid year-grid">
                <div className="years">
                  {years.map((year, index) => (
                    <button key={index} type="button"
                      className={`year ${isYearSelected(year) ? 'selected' : ''} ${isYearDisabled(year) ? 'disabled' : ''} ${isCurrentYear(year) ? 'today' : ''}`}
                      onClick={() => handleYearSelect(year)}
                      onMouseDown={(e) => e.preventDefault()}
                      disabled={isYearDisabled(year)}
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="custom-date-picker" ref={dropdownRef}>
      <div className="date-input-group">
        {label && <label>{label}:</label>}
        <div 
          className={`date-display ${isEditing ? 'editing' : ''}`}
          onClick={handleDisplayClick}
        >
          {isEditing ? (
            <input
              ref={inputRef}
              type="text"
              className="date-input"
              value={editValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onKeyDown={handleInputKeyDown}
              placeholder="YYYY-MM-DD"
            />
          ) : (
            <span>{formatDisplayDate(value)}</span>
          )}
          {calendarSvg}
        </div>
      </div>

      {isOpen && (
        <div className={`calendar-dropdown ${opensAbove ? 'opens-above' : ''}`}>
          <div className="calendar-header">
            <button 
              type="button"
              onClick={() => navigate(-1)}
              onMouseDown={(e) => e.preventDefault()}
              className="nav-button"
            >
              ‹
            </button>
            <h3 
              className={view !== 'year' ? 'clickable' : ''}
              onClick={handleHeaderClick}
              onMouseDown={(e) => e.preventDefault()}
            >
              {getHeaderText()}
            </h3>
            <button 
              type="button"
              onClick={() => navigate(1)}
              onMouseDown={(e) => e.preventDefault()}
              className="nav-button"
            >
              ›
            </button>
          </div>

          {view === 'date' && (
            <div className="calendar-grid">
              <div className="weekdays">
                {weekdays.map(day => (
                  <div key={day} className="weekday">
                    {day}
                  </div>
                ))}
              </div>

              <div className="days">
                {days.map((date, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`day ${!date ? 'empty' : ''} ${
                      isDateSelected(date) ? 'selected' : ''
                    } ${isDateDisabled(date) ? 'disabled' : ''} ${
                      isToday(date) ? 'today' : ''
                    }`}
                    onClick={() => date && handleDateSelect(date)}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={!date || isDateDisabled(date)}
                  >
                    {date && date.getDate()}
                  </button>
                ))}
              </div>
            </div>
          )}

          {view === 'month' && (
            <div className="calendar-grid month-grid">
              <div className="months">
                {monthsShort.map((month, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`month ${
                      isMonthSelected(currentMonth.getFullYear(), index) ? 'selected' : ''
                    } ${isMonthDisabled(currentMonth.getFullYear(), index) ? 'disabled' : ''} ${
                      isCurrentMonth(currentMonth.getFullYear(), index) ? 'today' : ''
                    }`}
                    onClick={() => handleMonthSelect(index)}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={isMonthDisabled(currentMonth.getFullYear(), index)}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>
          )}

          {view === 'year' && (
            <div className="calendar-grid year-grid">
              <div className="years">
                {years.map((year, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`year ${
                      isYearSelected(year) ? 'selected' : ''
                    } ${isYearDisabled(year) ? 'disabled' : ''} ${
                      isCurrentYear(year) ? 'today' : ''
                    }`}
                    onClick={() => handleYearSelect(year)}
                    onMouseDown={(e) => e.preventDefault()}
                    disabled={isYearDisabled(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker; 